import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const PreviewContainer = styled(Box)({
  position: 'relative',
  '& img': {
    maxWidth: '100%',
    height: 'auto',
  },
});

const PreviewText = styled(Typography)(({ x, y, color, weight, size }) => ({
  position: 'absolute',
  left: `${x}%`,
  top: `${y}%`,
  transform: 'translate(-50%, -50%)',
  color: color,
  fontWeight: weight,
  fontSize: `clamp(${size * 0.4}px, ${size * 0.12}vw, ${size}px)`, // Responsive scaling: min, preferred, max
  pointerEvents: 'none',
  textShadow: '1px 1px 2px rgba(255,255,255,0.8), -1px -1px 2px rgba(255,255,255,0.8)',
  whiteSpace: 'nowrap',
}));

const EmployeePage = () => {
  const { cardId } = useParams();
  const [cardData, setCardData] = useState(null);
  const [name, setName] = useState('');
  const [baseImageUrl, setBaseImageUrl] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const fetchCard = async () => {
      try {
        const response = await fetch(`/api/cards/${cardId}`);
        const data = await response.json();
        setCardData(data);
        // Get the base SVG image once
        const imageResponse = await fetch(`/api/cards/${cardId}/base-image`);
        const imageBlob = await imageResponse.blob();
        setBaseImageUrl(URL.createObjectURL(imageBlob));
      } catch (error) {
        console.error('Error fetching card:', error);
      }
    };

    fetchCard();
    
    // Cleanup
    return () => {
      if (baseImageUrl) {
        URL.revokeObjectURL(baseImageUrl);
      }
    };
  }, [cardId]);

  const handleDownload = async () => {
    if (!name.trim()) return;
    
    setIsDownloading(true);
    try {
      const response = await fetch(`/api/cards/${cardId}/download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: name.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to download image');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `celebration-card-${name.trim()}.png`;
      document.body.appendChild(link);
      link.click();
      
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading image:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!cardData || !baseImageUrl) {
    return (
      <Container>
        <Typography>Loading...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main' }}>
          Personalize Your Card
        </Typography>

        <Paper sx={{ p: 3 }}>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Your Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Box>

          <PreviewContainer>
            <img
              src={baseImageUrl}
              alt="Card Preview"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
            {name && (
              <PreviewText
                x={cardData.namePosition.x}
                y={cardData.namePosition.y}
                color={cardData.textStyle?.color || '#000000'}
                weight={cardData.textStyle?.fontWeight || 400}
                size={cardData.textStyle?.fontSize || 24}
              >
                {name}
              </PreviewText>
            )}
          </PreviewContainer>

          <Box sx={{ mt: 3 }}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleDownload}
              disabled={!name.trim() || isDownloading}
            >
              {isDownloading ? 'Downloading...' : 'Download Card'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default EmployeePage; 